<template>
  <UiDrawer v-model="isOpen" :title="$t('notifications.activityHistory')" width="400px">
    <div class="box-border flex flex-col h-full text-neutral-700 dark:text-neutral-400 px-4">
      <OverlayScrollbarsComponent defer class="overflow-auto mb-4 pr-4 -mr-4">
        <div v-if="midiaStore.uploadList.length" class="mb-4">
          <div class="p-2 flex justify-between items-center text-sm font-semibold">
            <span>Uploads</span>
            <span>{{ uploads.length }} de {{ midiaStore.uploadList.length }}</span>
          </div>
          <div v-for="item in uploads" :key="item.id" class="nofication-item">
            <UiUploadProgress :item="item" />
          </div>
          <div v-if="midiaStore.uploadList.length > 4" class="p-3">
            <RouterLink to="/notifications" @click="isOpen = false">
              {{ $t('notifications.uploadHistory') }}
            </RouterLink>
          </div>
        </div>

        <div v-if="!isSupported || userPermission === 'denied'" class="text-center mb-2">
          <UiAlert
            type="warning"
            :title="$t('notifications.unsupportedTitle')"
            :text="$t('notifications.unsupportedBody')"
          ></UiAlert>
        </div>
        <div v-else-if="userPermission === 'default'" class="mb-2">
          <UiAlert
            type="info"
            :title="$t('notifications.unsupportedTitle')"
            class="text-center"
          >
            {{ $t('notifications.alertEnable') }}
            <UiButton
              type="info"
              class="mt-2 p-2"
              @click="askConsent"
            >{{ $t('notifications.enable') }}</UiButton>
          </UiAlert>
        </div>
        <div class="flex flex-col gap-2 mb-4">
          <div class="flex justify-between items-center text-sm font-semibold">
            <span>{{ $t('labels.notification', 2) }}</span>
            <span>{{ uoNotifications.onlyUnread ? $t('notifications.unread') : $t('labels.all', 2) }} <pe-switch v-model="uoNotifications.onlyUnread" class="scale-75" /></span>
          </div>

          <div v-for="(group, index) in notificationList" :key="`${index}-${group.time}`">
            <div class="mb-1 font-semibold text-sm">{{ group.date.toLocaleDateString() }}</div>
            <div v-for="notification in group.notifications" :key="notification.id" :class="['nofication-item font-light', { 'unread' : notification.status === 'SENT' }]">
              <div class="mb-2 text-neutral-700 dark:text-neutral-400">
                <div class="mb-2" :class="{ 'font-semibold !text-neutral-800 dark:!text-neutral-300': notification.status === 'SENT' }">{{ notification.title }}</div>
                <div class="text-sm">{{ notification.content || '(Conteúdo em branco)' }}</div>
              </div>
              <div class="flex justify-between text-sm">
                <div class="-ml-1">
                  <UiButton
                    type="ghost"
                    class="!text-red-500"
                    @click="userNotifications.remove(notification.id)"
                  >{{ $t('button.delete') }}</UiButton>
                  <UiButton
                    v-if="notification.status === 'SENT'"
                    type="link"
                    class="mr-1"
                    @click="userNotifications.markAsRead(notification.id)"
                  >{{ $t('button.markAsRead') }}</UiButton>
                </div>
                <div class="text-neutral-600">{{ notification.deliveryTime ? new Date(notification.deliveryTime).toLocaleTimeString() : '-' }}</div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!notificationList?.length && !uploads.length" class="grow px-4">
          <div class="flex flex-col items-center gap-6 mt-12">
            <img :src="ImgAllClear" alt="" class="h-full w-full max-w-[200px]" />
            <div>
              <UiAlert
                type="warning"
                :title="$t('alerts.youIncredible')"
                :text="$t('alerts.noNotifications')"
              >
                <template #icon>
                  <div class="flex items-center">
                    <UiIcon icon="confetti" size="48" />
                  </div>
                </template>
              </UiAlert>
            </div>
          </div>
        </div>
      </OverlayScrollbarsComponent>

      <div class="my-4 flex items-center justify-between mt-auto pb-2 shrink-0">
        <div class="w-full flex flex-col gap-2">
          <RouterLink to="/notifications" @click="isOpen = false">
            {{ $t('notifications.history') }}
          </RouterLink>
          <!-- <RouterLink to="/gestao/historico-envios" @click="isOpen = false">
            {{ $t('notifications.uploadHistory') }}
          </RouterLink> -->
        </div>
        <UiButton
          type="primary"
          class="p-4"
          :loading="userNotifications.status === 'loading'"
          :disabled="userNotifications.status === 'loading'"
          @click="userNotifications.update()"
        >
          {{ $t('button.update') }}
        </UiButton>
      </div>
    </div>
  </UiDrawer>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch, nextTick } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-vue'
import { useMidiaStore, useUserNotificationsStore } from '@/stores'
import { status, init, subscribed, subscribe, /* unsubscribe */ } from '@/common/firebase'
import type { PushNotification } from '@/stores/userNotificationsStore'

import UiDrawer from '@/components/ui/drawer.vue'
import UiButton from '@/components/ui/button.vue'
import UiAlert from '@/components/ui/alert.vue'
import UiIcon from '@/components/ui/icon.vue'
import UiUploadProgress from './uploadProgress.vue'

import 'overlayscrollbars/overlayscrollbars.css'
import ImgAllClear from '@/assets/allclear.svg'

const midiaStore = useMidiaStore()
const userNotifications = useUserNotificationsStore()
const isOpen = ref(false)

const isSupported = computed(() => typeof Notification !== 'undefined')
const userPermission = ref(Notification.permission)

const uoNotifications = useLocalStorage<any>('uo.notifications', {
  onlyUnread: false
})

// Group notifications by date
const notificationList = computed(() => {
  let list : { date: Date, time: number, notifications: PushNotification[] }[] = []

  let baseList = userNotifications.state.list

  if (uoNotifications.value.onlyUnread) {
    baseList = baseList.filter((item) => item.status === 'SENT')
  }

  baseList.forEach((item) => {
      const date = new Date(item.deliveryTime)
      const time = date?.getTime() || 0

      const dateIndex = list.findIndex((group) => group.date.toDateString() === date.toDateString())

      if (dateIndex === -1) {
        list.push({ date, time, notifications: [item] })
      } else {
        list[dateIndex].notifications.push(item)
      }
    })

  return list
})

const uploads = computed(() => midiaStore.uploadList.slice(0, 4))

const open = () => {
  isOpen.value = true
}

const doSubscribe = () => {
  nextTick(() => {
    if (userPermission.value === 'granted' && status.value === 2 && !subscribed.value) {
      subscribe()
    }
  })
}

watch(
  () => status.value,
  (v) => {
    if (v === 2) doSubscribe()
  },
  { immediate: true }
)

const askConsent = async () => {
  if (isSupported.value && userPermission.value === 'default') {
    const consent = await Notification.requestPermission()
    userPermission.value = consent
    doSubscribe()
  }
}

onBeforeMount(() => {
  if (status.value === 0)
    init()

  if (userNotifications.status === 'initial')
    userNotifications.update({ page: 1, size: 16 })
})

defineExpose({
  open
})
</script>

<style lang="scss" scoped>
.nofication-item {
  @apply relative mb-1 flex flex-col border border-solid border-neutral-500/30 py-2 px-3 rounded-lg shadow-sm overflow-hidden;
}
.nofication-item.unread {
  @apply border-[--c-primary-500] shadow-[0_0_8px_2px_var(--c-primary-200)] dark:border-[--c-primary-800] dark:shadow-[0_0_10px__var(--c-primary-800)];
}
</style>
